import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { Device } from "@/types/device";
import { getDeviceInfo } from "@/lib/device";
import { getLocalInstallationId } from "@/lib/logger";
import {
  registerDevice,
  getMyAppDevices,
  deleteDevice as deleteDeviceApi,
} from "@/repository";
import { useAuthStore } from "@/stores/authStore";
import { debounce } from "@/lib/utils";
import logger from "@/lib/logger";

export const useDeviceStore = defineStore("device", () => {

  const currentDevice = ref<Device | null>(null);
  const devices = ref<Device[]>([]);
  const isLoadingDevices = ref(false);
  const loadDevicesError = ref<Error | null>(null);
  const deleteStates = ref<Record<number, "idle" | "loading" | "error">>({});
  const hasDeletedDevice = ref(false);

  const isDeviceLimitReached = computed(
    () => currentDevice.value !== null && currentDevice.value.activated_at === null
  );

  const showDeviceLimitWall = computed(
    () => isDeviceLimitReached.value && !!useAuthStore().user
  );

  const currentOsId = getLocalInstallationId();

  let registerInFlight: Promise<void> | null = null;

  async function register() {
    if (registerInFlight) {
      return registerInFlight;
    }

    registerInFlight = (async () => {
      try {
        const info = getDeviceInfo();
        const res = await registerDevice(info);
        currentDevice.value = res.data.data;
      } catch (err) {
        logger.error("Device registration failed", err);
      } finally {
        registerInFlight = null;
      }
    })();

    return registerInFlight;
  }

  const registerDebounced = debounce(() => register(), 300);

  async function fetchDevices() {
    isLoadingDevices.value = true;
    loadDevicesError.value = null;
    try {
      const res = await getMyAppDevices();
      devices.value = res.data.data;
      deleteStates.value = {};
      hasDeletedDevice.value = false;
    } catch (err) {
      loadDevicesError.value = err as Error;
      logger.error("Failed to fetch devices", err);
    } finally {
      isLoadingDevices.value = false;
    }
  }

  async function removeDevice(id: number) {
    deleteStates.value[id] = "loading";
    try {
      await deleteDeviceApi(id);
      devices.value = devices.value.filter((d) => d.id !== id);
      delete deleteStates.value[id];
      hasDeletedDevice.value = true;
    } catch (err) {
      deleteStates.value[id] = "error";
      logger.error("Failed to delete device", err);
    }
  }

  async function continueAfterDelete() {
    return register();
  }

  function reset() {
    currentDevice.value = null;
    devices.value = [];
    isLoadingDevices.value = false;
    loadDevicesError.value = null;
    deleteStates.value = {};
    hasDeletedDevice.value = false;
  }

  registerDebounced();

  return {
    currentDevice,
    devices,
    isLoadingDevices,
    loadDevicesError,
    deleteStates,
    hasDeletedDevice,
    isDeviceLimitReached,
    showDeviceLimitWall,
    currentOsId,
    register: registerDebounced,
    fetchDevices,
    removeDevice,
    continueAfterDelete,
    reset,
  };
});
